import type { ResaltadoDeHoja } from "@gestiondoc/plantilla";
import {
  type Ancla,
  NOMBRE_TIPO_CORRECCION,
  TIPOS_DE_CORRECCION,
  type TipoDeCorreccion,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { conAviso, useTRPC } from "../api.ts";
import { useFlujogramas } from "../editor/mermaid.ts";
import { ANCHO_GLOBO, type Lugar, seleccionEnLaHoja } from "../editor/seleccion.ts";
import { datosDeHoja } from "../editor/VistaDeContenido.tsx";
import { VistaHoja } from "../editor/VistaHoja.tsx";
import type { Marca } from "../tipos.ts";
import { COLOR_TIPO } from "./PanelDeMarcas.tsx";
import { Boton, CLASE_ENTRADA, EstadoVacio } from "./ui.tsx";

/**
 * Fase 4 · la hoja redactada con las marcas pintadas. Marcar es como comentar en Word: se
 * selecciona una frase, junto a ella aparece un globo con los tres tipos, y al elegir uno se
 * escribe el comentario ahí mismo, sin ir al panel. La selección se lee cuando termina (al soltar
 * el ratón, el dedo o el lápiz), no a mitad del arrastre; y lo que no se puede marcar lo dice, en
 * vez de no hacer nada.
 */

/** La frase seleccionada que espera su marca. Con `tipo`, ya se está escribiendo el comentario. */
type Pendiente = { anclas: Ancla[]; lugar: Lugar; tipo: TipoDeCorreccion | null };

export function HojaConMarcas({
  revisionId,
  marcas,
  activa,
  puedeMarcar,
  siguienteNumero,
  onElegir,
  onAviso,
}: {
  revisionId: string;
  marcas: Marca[];
  activa: number | null;
  puedeMarcar: boolean;
  /** El número que llevará la próxima marca: la frase pendiente se pinta ya con él. */
  siguienteNumero: number;
  onElegir: (n: number) => void;
  onAviso: (m: string | null) => void;
}) {
  const trpc = useTRPC();
  const q = useQuery(trpc.contenido.obtener.queryOptions({ revisionId }));
  const svgs = useFlujogramas(q.data?.contenido);
  const marco = useRef<HTMLDivElement>(null);
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);
  /** Por qué no se pudo marcar lo seleccionado (varios párrafos, una tabla…). */
  const [pista, setPista] = useState<{ texto: string; lugar: Lugar } | null>(null);
  const [comentario, setComentario] = useState("");
  const comentarioRef = useRef(comentario);
  comentarioRef.current = comentario;

  const cerrar = useCallback(() => {
    setPendiente(null);
    setPista(null);
    setComentario("");
  }, []);

  const resaltados = useMemo<ResaltadoDeHoja[]>(
    () =>
      // La frase que se está comentando va primero: se pinta aunque toque una marca existente.
      (pendiente?.tipo
        ? pendiente.anclas.map((a, i) => ({
            bloqueId: a.bloqueId,
            inicio: a.inicio,
            fin: a.fin,
            numero: siguienteNumero,
            tipo: pendiente.tipo as TipoDeCorreccion,
            activo: true,
            sinNumero: i > 0,
          }))
        : []
      ).concat(
        marcas.flatMap((x) => {
          if (!x.ancla) return [];
          const titulo = `${NOMBRE_TIPO_CORRECCION[x.tipo]} ${x.numero} · ${x.autor.nombre}\n${x.comentario}`;
          return [{ ancla: x.ancla, lugar: x.lugar }, ...(x.tramos ?? [])].flatMap((t, i) =>
            t.lugar
              ? [
                  {
                    bloqueId: t.ancla.bloqueId,
                    inicio: t.lugar.inicio,
                    fin: t.lugar.fin,
                    numero: x.numero,
                    tipo: x.tipo,
                    activo: x.numero === activa,
                    titulo,
                    sinNumero: i > 0,
                  },
                ]
              : [],
          );
        }),
      ),
    [marcas, activa, pendiente, siguienteNumero],
  );

  const contenido = q.data?.contenido;
  const leerSeleccion = useCallback(() => {
    const caja = marco.current;
    if (!puedeMarcar || !contenido || !caja) return;
    const leida = seleccionEnLaHoja(caja, contenido);
    if (leida.que === "nada") return;
    if (leida.que === "problema") {
      // Con un comentario a medio escribir no se pierde nada por una selección que no sirve.
      if (!comentarioRef.current.trim()) setPendiente(null);
      setPista({ texto: leida.texto, lugar: leida.lugar });
      return;
    }
    setPista(null);
    // Otra frase con el comentario ya empezado: se mueve la marca y se conserva lo escrito.
    setPendiente((antes) => ({
      anclas: leida.anclas,
      lugar: leida.lugar,
      tipo: antes?.tipo ?? null,
    }));
  }, [puedeMarcar, contenido]);

  useEffect(() => {
    if (!puedeMarcar) return;
    const caja = marco.current;
    let arrastrando = false;
    let t: ReturnType<typeof setTimeout> | undefined;
    const enLaHoja = (n: EventTarget | null) =>
      n instanceof Node && !!caja?.contains(n) && !(n as Element).closest?.("[data-globo]");
    const alBajar = (e: PointerEvent) => {
      if (!enLaHoja(e.target)) return;
      arrastrando = true;
      setPista(null);
    };
    const alSoltar = () => {
      if (!arrastrando) return;
      arrastrando = false;
      // Un momento después: el navegador termina de fijar la selección al soltar.
      clearTimeout(t);
      t = setTimeout(() => {
        const sel = window.getSelection();
        if (sel?.isCollapsed) {
          // Un clic suelto en la hoja cierra el globo, salvo que ya se esté escribiendo.
          if (!comentarioRef.current.trim()) setPendiente((p) => (p?.tipo ? p : null));
          return;
        }
        leerSeleccion();
      }, 10);
    };
    // Las asas de selección del teléfono y el teclado (Mayús + flechas) no sueltan ningún
    // puntero: para esos, se espera a que la selección deje de moverse.
    const alCambiar = () => {
      if (arrastrando) return;
      clearTimeout(t);
      t = setTimeout(leerSeleccion, 400);
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") cerrar();
    };
    document.addEventListener("pointerdown", alBajar);
    document.addEventListener("pointerup", alSoltar);
    document.addEventListener("pointercancel", alSoltar);
    document.addEventListener("selectionchange", alCambiar);
    document.addEventListener("keydown", alTeclear);
    return () => {
      clearTimeout(t);
      document.removeEventListener("pointerdown", alBajar);
      document.removeEventListener("pointerup", alSoltar);
      document.removeEventListener("pointercancel", alSoltar);
      document.removeEventListener("selectionchange", alCambiar);
      document.removeEventListener("keydown", alTeclear);
    };
  }, [puedeMarcar, leerSeleccion, cerrar]);

  const crear = useMutation(trpc.correcciones.crear.mutationOptions(conAviso(onAviso, cerrar)));

  if (q.isPending) return <EstadoVacio>Cargando el documento…</EstadoVacio>;
  if (q.isError) return <EstadoVacio>{q.error.message}</EstadoVacio>;
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: las marcas también se eligen desde el panel, con teclado
    // biome-ignore lint/a11y/noStaticElementInteractions: ídem
    <div
      ref={marco}
      className="relative rounded-tarjeta bg-gris-fondo p-2 sm:p-4"
      onClick={(e) => {
        const n = (e.target as Element).closest("mark[data-correccion]");
        const numero = n ? Number(n.getAttribute("data-correccion")) : null;
        if (numero && marcas.some((x) => x.numero === numero)) onElegir(numero);
      }}
    >
      <VistaHoja datos={{ ...datosDeHoja(q.data, q.data.contenido), svgs, resaltados }} />
      {pista && (
        <Globo lugar={pista.lugar}>
          <p className="px-3 py-2 text-[12.5px] text-texto-2">{pista.texto}</p>
        </Globo>
      )}
      {pendiente && !pendiente.tipo && (
        <Globo lugar={pendiente.lugar} ancho="auto">
          <div className="flex items-center gap-1 p-1.5">
            <span className="px-1.5 text-[11.5px] font-semibold text-texto-3">Marcar:</span>
            {TIPOS_DE_CORRECCION.map((t) => (
              <button
                key={t}
                type="button"
                // Sin esto, pulsar el botón deshace la selección antes de leerla.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setPendiente({ ...pendiente, tipo: t });
                  // La frase queda pintada como marca; la selección azul ya no hace falta.
                  window.getSelection()?.removeAllRanges();
                }}
                className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12.5px] font-semibold text-marino hover:bg-marino-claro"
              >
                <span className={`h-2.5 w-2.5 rounded-sm ${COLOR_TIPO[t]}`} />
                {NOMBRE_TIPO_CORRECCION[t]}
              </button>
            ))}
          </div>
        </Globo>
      )}
      {pendiente?.tipo && (
        <Globo lugar={pendiente.lugar}>
          <form
            className="space-y-2 p-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!pendiente.tipo || !comentario.trim() || crear.isPending) return;
              const [ancla, ...mas] = pendiente.anclas;
              if (!ancla) return;
              crear.mutate({ revisionId, tipo: pendiente.tipo, comentario, ancla, mas });
            }}
          >
            <blockquote className="max-h-16 overflow-hidden text-[12.5px] italic text-texto-2">
              «{pendiente.anclas[0]?.citado}»
              {pendiente.anclas.length > 1 && (
                <span className="not-italic text-texto-3">
                  {" "}
                  … y {pendiente.anclas.length - 1} tramo{pendiente.anclas.length > 2 ? "s" : ""}{" "}
                  más
                </span>
              )}
            </blockquote>
            <div className="flex gap-1">
              {TIPOS_DE_CORRECCION.map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={pendiente.tipo === t}
                  onClick={() => setPendiente({ ...pendiente, tipo: t })}
                  className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px] font-semibold ${
                    pendiente.tipo === t
                      ? "border-marino bg-marino-claro text-marino"
                      : "border-borde text-texto-3"
                  }`}
                >
                  <span className={`h-2 w-2 rounded-sm ${COLOR_TIPO[t]}`} />
                  {NOMBRE_TIPO_CORRECCION[t]}
                </button>
              ))}
            </div>
            <textarea
              // biome-ignore lint/a11y/noAutofocus: se abre al elegir el tipo, para escribir enseguida
              autoFocus
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey))
                  e.currentTarget.form?.requestSubmit();
              }}
              rows={3}
              placeholder="Qué hay que cambiar, o la pregunta"
              aria-label="Comentario de la marca"
              className={CLASE_ENTRADA}
            />
            <div className="flex items-center justify-end gap-2">
              <span className="mr-auto text-[11px] text-texto-3">Ctrl + Enter guarda</span>
              <Boton variante="secundario" onClick={cerrar} disabled={crear.isPending}>
                Cancelar
              </Boton>
              <Boton type="submit" disabled={crear.isPending || !comentario.trim()}>
                {crear.isPending ? "Guardando…" : "Guardar marca"}
              </Boton>
            </div>
          </form>
        </Globo>
      )}
    </div>
  );
}

/** El globo que sale junto a la frase seleccionada (no se selecciona ni cuenta como hoja). */
function Globo({
  lugar,
  ancho = ANCHO_GLOBO,
  children,
}: {
  lugar: Lugar;
  ancho?: number | "auto";
  children: React.ReactNode;
}) {
  return (
    <div
      data-globo=""
      className="absolute z-20 select-none rounded-tarjeta border border-borde bg-white shadow-lg"
      style={{
        top: lugar.arriba,
        left: lugar.izquierda,
        width: ancho,
        maxWidth: "calc(100% - 16px)",
      }}
    >
      {children}
    </div>
  );
}
